import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiPlay, FiPlus, FiCheck, FiStar, FiClock, FiCalendar,
  FiArrowLeft, FiYoutube, FiBookmark,
} from 'react-icons/fi';
import { fetchMovieDetails, IMG_BASE } from '../api/tmdb';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useMovieContext } from '../context/MovieContext';
import { useAuth } from '../context/AuthContext';
import MovieCard from '../components/MovieCard';
import Loader from '../components/Loader';
import TrailerModal from '../components/TrailerModal';
import ReviewSection from '../components/ReviewSection';

const Badge = ({ children }) => (
  <span className="px-3 py-1 rounded-full text-xs font-body font-medium"
    style={{ background: 'rgba(255,255,255,0.07)', color: 'var(--text)', border: '1px solid rgba(255,255,255,0.08)' }}>
    {children}
  </span>
);

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex flex-col gap-1">
    <div className="flex items-center gap-1.5" style={{ color: 'var(--muted)' }}>
      <Icon size={12} /><span className="text-xs font-body">{label}</span>
    </div>
    <span className="text-sm font-semibold font-body" style={{ color: 'var(--text)' }}>{value}</span>
  </div>
);

const ContinueBar = ({ progress, duration, onSeek }) => {
  if (!progress || !duration) return null;
  const pct = Math.min((progress / duration) * 100, 100);
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-body" style={{ color: 'var(--muted)' }}>Continue watching</span>
        <span className="text-xs font-accent" style={{ color: 'var(--red)' }}>
          {Math.floor(progress / 60)}m {Math.floor(progress % 60)}s left
        </span>
      </div>
      <div className="relative h-1.5 rounded-full overflow-hidden cursor-pointer"
        style={{ background: 'rgba(255,255,255,0.1)' }} onClick={onSeek}>
        <motion.div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'var(--red)' }}
          initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }} />
      </div>
    </div>
  );
};

const MovieDetailPage = () => {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { addToWatchlist, removeFromWatchlist, isInWatchlist } = useMovieContext();
  const { user, saveProgress, getProgress } = useAuth();

  const [movie,        setMovie]       = useState(null);
  const [customMovie,  setCustomMovie] = useState(null);
  const [loading,      setLoading]     = useState(true);
  const [trailerKey,   setTrailerKey]  = useState(null);
  const [showTrailer,  setShowTrailer] = useState(false);
  const [imgLoaded,    setImgLoaded]   = useState(false);
  const [progress,     setProgress]    = useState(0);
  const [playerTime,   setPlayerTime]  = useState(0);
  const saveTimer = useRef();

  const isCustomId = String(id).startsWith('custom_');
  const rawId      = isCustomId ? id.replace('custom_', '') : id;

  // ── Load movie data ──────────────────────────────────────────────
  useEffect(() => {
    setLoading(true); setImgLoaded(false); setMovie(null); setCustomMovie(null);
    window.scrollTo(0, 0);

    if (isCustomId) {
      getDoc(doc(db, 'customMovies', rawId)).then(snap => {
        if (snap.exists()) setCustomMovie({ id: snap.id, ...snap.data() });
      }).finally(() => setLoading(false));
    } else {
      fetchMovieDetails(rawId).then(res => {
        setMovie(res.data);
        const t = res.data.videos?.results?.find(v => v.type === 'Trailer' && v.site === 'YouTube');
        setTrailerKey(t?.key || null);
      }).catch(console.error).finally(() => setLoading(false));
    }
  }, [id, isCustomId, rawId]);

  // ── Load saved progress ──────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    getProgress(rawId).then(s => { setProgress(s); setPlayerTime(s); });
  }, [user, rawId, getProgress]);

  // ── Auto-save progress every 10 s while trailer plays ───────────
  const handlePlayerProgress = useCallback((seconds) => {
    setPlayerTime(seconds);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveProgress(rawId, seconds), 10000);
  }, [rawId, saveProgress]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  if (loading) return <Loader full />;

  // ── Custom movie detail ──────────────────────────────────────────
  if (isCustomId && customMovie) {
    const cm = customMovie;
    return (
      <div style={{ background: 'var(--dark)', minHeight: '100vh' }}>
        <div className="relative w-full overflow-hidden" style={{ height: '65vh', minHeight: 420 }}>
          {cm.backdropUrl && (
            <img src={cm.backdropUrl} alt={cm.title}
              className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'brightness(0.35)' }} />
          )}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right,rgba(10,10,15,0.98) 0%,rgba(10,10,15,0.45) 55%,transparent 85%)' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top,var(--dark) 0%,transparent 55%)' }} />

          <motion.button initial={{ x: -24, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
            whileHover={{ x: -4 }} onClick={() => navigate(-1)}
            className="absolute top-24 left-6 md:left-12 flex items-center gap-2 px-4 py-2 rounded-full glass z-10"
            style={{ color: 'var(--text)', fontSize: 13 }}>
            <FiArrowLeft size={15} /> Back
          </motion.button>

          <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 pb-10 flex gap-8 items-end">
            {cm.posterUrl && (
              <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}
                className="hidden md:block flex-shrink-0 rounded-2xl overflow-hidden"
                style={{ width: 170, height: 255, border: '2px solid rgba(229,9,20,0.4)', boxShadow: '0 0 40px rgba(229,9,20,0.2)' }}>
                <img src={cm.posterUrl} alt={cm.title} className="w-full h-full object-cover" />
              </motion.div>
            )}
            <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }} className="flex-1">
              {cm.genre && (
                <div className="flex gap-2 mb-3">
                  {cm.genre.split(',').map(g => <Badge key={g}>{g.trim()}</Badge>)}
                </div>
              )}
              <h1 className="font-display text-4xl md:text-6xl leading-none mb-3" style={{ color: 'var(--text)' }}>{cm.title}</h1>
              <div className="flex items-center gap-5 mb-4 flex-wrap">
                {cm.rating > 0 && (
                  <div className="flex items-center gap-1.5" style={{ color: 'var(--gold)' }}>
                    <FiStar size={16} fill="currentColor" />
                    <span className="font-accent font-bold text-lg">{cm.rating}</span>
                    <span className="text-xs" style={{ color: 'var(--muted)' }}>/10</span>
                  </div>
                )}
                {cm.releaseYear && <Stat icon={FiCalendar} label="Year" value={cm.releaseYear} />}
              </div>
              <ContinueBar progress={progress} duration={7200} onSeek={() => setShowTrailer(true)} />
              <div className="flex items-center gap-3 mt-5 flex-wrap">
                {cm.youtubeId && (
                  <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                    onClick={() => setShowTrailer(true)}
                    className="flex items-center gap-2 px-7 py-3 rounded-full font-body font-semibold text-sm"
                    style={{ background: 'var(--red)', color: '#fff', boxShadow: '0 0 30px rgba(229,9,20,0.4)' }}>
                    <FiYoutube size={16} /> Watch Now
                  </motion.button>
                )}
              </div>
            </motion.div>
          </div>
        </div>

        <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-10">
          {cm.description && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
              <h3 className="font-display text-2xl mb-3 tracking-wide" style={{ color: 'var(--text)' }}>Overview</h3>
              <p className="font-body leading-relaxed max-w-3xl" style={{ color: 'rgba(232,232,240,0.75)', fontSize: 15 }}>{cm.description}</p>
            </motion.div>
          )}
          <ReviewSection movieId={'custom_' + cm.id} />
        </div>

        <TrailerModal videoKey={showTrailer ? cm.youtubeId : null} onClose={() => { setShowTrailer(false); saveProgress(rawId, playerTime); }} />
      </div>
    );
  }

  // ── TMDB movie detail ────────────────────────────────────────────
  if (!movie) return null;
  const inWatchlist = isInWatchlist(movie.id);
  const runtime  = movie.runtime ? `${Math.floor(movie.runtime / 60)}h ${movie.runtime % 60}m` : 'N/A';
  const cast     = movie.credits?.cast?.slice(0, 10) || [];
  const similar  = movie.similar?.results?.slice(0, 12) || [];
  const director = movie.credits?.crew?.find(c => c.job === 'Director');

  return (
    <div style={{ background: 'var(--dark)', minHeight: '100vh' }}>
      {/* Banner */}
      <div className="relative w-full overflow-hidden" style={{ height: '70vh', minHeight: 480 }}>
        {movie.backdrop_path && (
          <motion.img src={IMG_BASE + '/original' + movie.backdrop_path} alt={movie.title}
            onLoad={() => setImgLoaded(true)}
            initial={{ scale: 1.08, opacity: 0 }}
            animate={{ scale: imgLoaded ? 1 : 1.08, opacity: imgLoaded ? 1 : 0 }}
            transition={{ duration: 0.8 }}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.35)' }} />
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right,rgba(10,10,15,0.98) 0%,rgba(10,10,15,0.5) 50%,transparent 82%)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top,var(--dark) 0%,transparent 52%)' }} />

        <motion.button initial={{ x: -24, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
          whileHover={{ x: -4 }} onClick={() => navigate(-1)}
          className="absolute top-24 left-6 md:left-12 flex items-center gap-2 px-4 py-2 rounded-full glass z-10"
          style={{ color: 'var(--text)', fontSize: 13 }}>
          <FiArrowLeft size={15} /> Back
        </motion.button>

        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 pb-10 flex gap-8 items-end">
          <motion.div initial={{ y: 28, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}
            className="hidden md:block flex-shrink-0 rounded-2xl overflow-hidden shadow-2xl"
            style={{ width: 180, height: 270, border: '2px solid rgba(229,9,20,0.4)', boxShadow: '0 0 40px rgba(229,9,20,0.2)' }}>
            {movie.poster_path && <img src={IMG_BASE + '/w342' + movie.poster_path} alt={movie.title} className="w-full h-full object-cover" />}
          </motion.div>

          <motion.div initial={{ y: 28, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }} className="flex-1">
            <div className="flex flex-wrap gap-2 mb-3">
              {movie.genres?.map(g => <Badge key={g.id}>{g.name}</Badge>)}
            </div>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl leading-none mb-3"
              style={{ color: 'var(--text)', textShadow: '0 0 60px rgba(229,9,20,0.15)' }}>
              {movie.title}
            </h1>
            <div className="flex items-center gap-6 mb-4 flex-wrap">
              <div className="flex items-center gap-1.5" style={{ color: 'var(--gold)' }}>
                <FiStar size={16} fill="currentColor" />
                <span className="font-accent font-bold text-lg">{movie.vote_average?.toFixed(1)}</span>
                <span className="text-xs" style={{ color: 'var(--muted)' }}>/10</span>
              </div>
              <Stat icon={FiClock} label="Runtime" value={runtime} />
              <Stat icon={FiCalendar} label="Released" value={movie.release_date?.split('-')[0]} />
              {director && (
                <div className="flex flex-col gap-1">
                  <span className="text-xs" style={{ color: 'var(--muted)' }}>Director</span>
                  <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{director.name}</span>
                </div>
              )}
            </div>

            {/* Continue watching bar */}
            <ContinueBar progress={progress} duration={movie.runtime ? movie.runtime * 60 : null} onSeek={() => setShowTrailer(true)} />

            <div className="flex items-center gap-3 mt-5 flex-wrap">
              {trailerKey && (
                <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                  onClick={() => setShowTrailer(true)}
                  className="flex items-center gap-2 px-7 py-3 rounded-full font-body font-semibold text-sm"
                  style={{ background: 'var(--red)', color: '#fff', boxShadow: '0 0 30px rgba(229,9,20,0.4)' }}>
                  <FiPlay size={15} fill="currentColor" /> Play Trailer
                </motion.button>
              )}
              <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                onClick={() => inWatchlist ? removeFromWatchlist(movie.id) : addToWatchlist(movie)}
                className="flex items-center gap-2 px-7 py-3 rounded-full font-body font-semibold text-sm glass hover:bg-white/10 transition-colors"
                style={{ color: inWatchlist ? 'var(--gold)' : 'var(--text)' }}>
                {inWatchlist ? <FiCheck size={15} /> : <FiPlus size={15} />}
                {inWatchlist ? 'Saved' : 'Watchlist'}
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-screen-xl mx-auto px-6 md:px-12 py-10">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="mb-12">
          <h3 className="font-display text-2xl mb-3 tracking-wide" style={{ color: 'var(--text)' }}>Overview</h3>
          <p className="font-body leading-relaxed max-w-3xl" style={{ color: 'rgba(232,232,240,0.75)', fontSize: 15 }}>{movie.overview}</p>
        </motion.div>

        {cast.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mb-12">
            <h3 className="font-display text-2xl mb-5 tracking-wide" style={{ color: 'var(--text)' }}>Cast</h3>
            <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-2">
              {cast.map(p => (
                <div key={p.id} className="flex-shrink-0 text-center" style={{ width: 86 }}>
                  <div className="w-16 h-16 rounded-full overflow-hidden mx-auto mb-2 border-2" style={{ borderColor: 'var(--border)' }}>
                    {p.profile_path
                      ? <img src={IMG_BASE + '/w185' + p.profile_path} alt={p.name} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center glass font-display text-lg" style={{ color: 'var(--red)' }}>{p.name[0]}</div>
                    }
                  </div>
                  <p className="text-xs font-body font-medium leading-tight" style={{ color: 'var(--text)' }}>{p.name}</p>
                  <p className="text-xs mt-0.5 line-clamp-1" style={{ color: 'var(--muted)' }}>{p.character}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { label: 'Status',   value: movie.status },
            { label: 'Language', value: movie.original_language?.toUpperCase() },
            { label: 'Budget',   value: movie.budget  ? `$${(movie.budget  / 1e6).toFixed(1)}M` : 'N/A' },
            { label: 'Revenue',  value: movie.revenue ? `$${(movie.revenue / 1e6).toFixed(1)}M` : 'N/A' },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-xl p-4 glass">
              <p className="text-xs mb-1 font-body" style={{ color: 'var(--muted)' }}>{label}</p>
              <p className="text-sm font-semibold font-body" style={{ color: 'var(--text)' }}>{value || 'N/A'}</p>
            </div>
          ))}
        </motion.div>

        {similar.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="mb-14">
            <h3 className="font-display text-2xl mb-5 tracking-wide" style={{ color: 'var(--text)' }}>Similar Movies</h3>
            <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-4">
              {similar.map(m => <MovieCard key={m.id} movie={m} />)}
            </div>
          </motion.div>
        )}

        {/* Reviews */}
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}>
          <ReviewSection movieId={String(movie.id)} />
        </motion.div>
      </div>

      <TrailerModal
        videoKey={showTrailer ? trailerKey : null}
        onClose={() => { setShowTrailer(false); saveProgress(rawId, playerTime); }}
        onProgress={handlePlayerProgress}
        startAt={playerTime}
      />
    </div>
  );
};

export default MovieDetailPage;
